/**
 * Lightweight in-memory cache for /api/lexical/surah/:n/words.
 *
 * - Key: surahNumber
 * - Value: { ayahKey: [wordDoc, ...] }
 *
 * Cache is process-lifetime only; cleared on reload. We keep at most
 * `MAX_SURAHS` surahs cached to bound memory.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { lexicalApi } from '../services/api';

const MAX_SURAHS = 4;
const _cache = new Map(); // surahNumber → { ayahs }
const _inflight = new Map(); // surahNumber → Promise

async function loadSurah(surahNumber) {
  if (_cache.has(surahNumber)) return _cache.get(surahNumber);
  if (_inflight.has(surahNumber)) return _inflight.get(surahNumber);
  const promise = (async () => {
    try {
      const res = await lexicalApi.getSurahWords(surahNumber);
      const data = res.data || { ayahs: {} };
      // LRU eviction
      if (_cache.size >= MAX_SURAHS) {
        const oldestKey = _cache.keys().next().value;
        _cache.delete(oldestKey);
      }
      _cache.set(surahNumber, data);
      return data;
    } catch (e) {
      // Empty fallback so the UI keeps working when nothing is imported yet
      const empty = { surahNumber, totalWords: 0, ayahs: {} };
      _cache.set(surahNumber, empty);
      return empty;
    } finally {
      _inflight.delete(surahNumber);
    }
  })();
  _inflight.set(surahNumber, promise);
  return promise;
}

/**
 * React hook: returns words for `ayahKey` from the cached surah dataset.
 *  - words: array of segment docs, in display order (wordIndex asc, segmentIndex asc)
 *  - byWord: Map<wordIndex, [segments…]>
 *  - loading
 */
export function useSurahLexicalWords(surahNumber, ayahKey) {
  const [data, setData] = useState(() => _cache.get(surahNumber) || null);
  const [loading, setLoading] = useState(!_cache.has(surahNumber));
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!surahNumber) return;
    if (_cache.has(surahNumber)) {
      setData(_cache.get(surahNumber));
      setLoading(false);
      return;
    }
    setLoading(true);
    loadSurah(surahNumber).then((d) => {
      if (mountedRef.current) {
        setData(d);
        setLoading(false);
      }
    });
    return () => { mountedRef.current = false; };
  }, [surahNumber]);

  return useMemo(() => {
    const ayahs = data?.ayahs || {};
    const words = ayahKey ? (ayahs[ayahKey] || []) : [];
    const byWord = new Map();
    for (const seg of words) {
      const k = seg.wordIndex;
      if (!byWord.has(k)) byWord.set(k, []);
      byWord.get(k).push(seg);
    }
    return { words, byWord, loading, hasData: words.length > 0 };
  }, [data, ayahKey, loading]);
}

export function clearLexicalCache() {
  _cache.clear();
  _inflight.clear();
}
